import { useState } from "react";
import { mdiAttachment, mdiTrayArrowDown } from "@mdi/js";
import Icon from "@mdi/react";

import Button from "components/Button";
import ChunkUploadFile from "components/ChunkUploadFile";
import Modal from "components/Modal";
import { VisibilitySVGIcon } from "components/SVGIcons";
import { NEW_CARRIER_CATEGORY } from "constants/base";
import { UPLOAD_DOCUMENT_URLS } from "constants/routes";
import UploadItem from "features/main/Loads/modules/UploadItem";
import { UploadedFile } from "models/Component";
import { IFactoringPaymentProfile } from "models/HaulPay";
import FMCSALink from "components/FMCSALink";

type CarrierInfoModalProps = {
  isOpen: boolean;
  closeModal: VoidFunction;
  carrierInfo: IFactoringPaymentProfile;
};

export default function CarrierInfoModal({
  isOpen,
  closeModal,
  carrierInfo
}: Readonly<CarrierInfoModalProps>) {
  const [uploadedFiles, setUploadedFiles] = useState<UploadedFile[]>([]);

  return (
    <Modal
      isOpen={isOpen}
      closeModal={() => {
        closeModal();
        setUploadedFiles([]);
      }}
      wrapperClass="!pt-0"
      dialogClass="!bg-modalBg !pt-6 !mt-20 !gap-5"
      closeClass="!top-24 !right-6 !bg-transparent !text-text">
      <div className="flex flex-col gap-1 pb-3 border-b border-subtitle">
        <h4 className="text-xl font-semibold desktop:text-2xl">Carrier’s info</h4>
        <FMCSALink
          dot={carrierInfo.payment_profile.company_dot}
          mc={carrierInfo.payment_profile.company_mc}>
          FMCSA Profile
        </FMCSALink>
      </div>

      <ul className="flex flex-col h-full gap-5 pb-5 overflow-auto text-xs border-b border-subtitle desktop:text-sm">
        <li className="flex flex-col gap-1">
          <span>Carrier Name</span>
          <span className="text-base font-bold">{carrierInfo.payment_profile.company_name}</span>
        </li>
        <li className="flex flex-col gap-1">
          <span>MC</span>
          <span className="text-base font-bold">{carrierInfo.payment_profile.company_mc}</span>
        </li>
        <li className="flex flex-col gap-1">
          <span>Owner Name</span>
          <span className="text-base font-bold">
            {carrierInfo.payment_profile.owner_name_first}
          </span>
        </li>
        <li className="flex flex-col gap-1">
          <span>Carrier Phone</span>
          <span className="text-base font-bold">
            {carrierInfo.payment_profile.owner_phone_number_cell}
          </span>
        </li>
        <li className="flex flex-col gap-1">
          <span>Carrier Email</span>
          <span className="text-base font-bold break-words">
            {carrierInfo.payment_profile.owner_email}
          </span>
        </li>
        <li className="flex flex-col gap-1">
          <span>Address</span>
          <span className="text-base font-bold break-words">
            {carrierInfo.payment_profile.owner_address_street_one}
          </span>
        </li>
        <li className="flex flex-col gap-1">
          <span>City</span>
          <span className="text-base font-bold">
            {carrierInfo.payment_profile.owner_address_city}
          </span>
        </li>
        <li className="flex flex-col gap-1">
          <span>ZIP Code</span>
          <span className="text-base font-bold">
            {" "}
            {carrierInfo.payment_profile.owner_address_zip}
          </span>
        </li>
        <li className="flex flex-col gap-1">
          <span>State</span>
          <span className="text-base font-bold">
            {carrierInfo.payment_profile.owner_address_state}
          </span>
        </li>

        <li className="flex flex-col gap-1">
          <span>Carriers NOA</span>
          <div className="flex gap-2.5">
            <div className="flex gap-2 min-h-8 pl-3 pr-8 *:my-auto bg-transparent desktop:text-iconButton border rounded border-tertiary desktop:border-subtitle">
              <Icon path={mdiAttachment} className="flex-none -rotate-30 text-placeholder size-4" />
              <span>Carriers NOA</span>
            </div>
            <button className="w-12 h-8 text-text desktop:text-modalBg rounded bg-foreground desktop:bg-iconDarkBtn *:m-auto">
              <VisibilitySVGIcon />
            </button>
            <button className="w-12 h-8 text-text desktop:text-modalBg rounded bg-foreground desktop:bg-iconDarkBtn *:m-auto">
              <Icon path={mdiTrayArrowDown} />
            </button>
          </div>
        </li>

        <ChunkUploadFile
          categories={NEW_CARRIER_CATEGORY}
          className="flex-none px-5 w-fit"
          multiple
          visibility={false}
          urlCallback={
            UPLOAD_DOCUMENT_URLS({
              payment_profile_id: carrierInfo?.payment_profile_id
            }).NEW_CARRIER
          }
          onUploadFinished={(files: any) => {
            const newCarrierFiles: UploadedFile[] = files.map((file: any) => ({
              name: file.name,
              size: file.fileSize,
              type: file.fileType,
              fileType:
                file.categories && file.categories.length > 0 ? file.categories.split(",") : [""],
              url: file.preview_url
            }));
            setUploadedFiles([...uploadedFiles, ...newCarrierFiles]);
          }}
        />
        {uploadedFiles.map((file) => (
          <div key={file.name} className="mx-3 border border-primary rounded-base">
            <UploadItem file={file} />
          </div>
        ))}
      </ul>

      <footer className="flex justify-end mt-5">
        <Button type="button" rounded onClick={closeModal}>
          Done
        </Button>
      </footer>
    </Modal>
  );
}
